import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation } from 'react-router-dom';
import {
  DASHBOARD_NAV_COLLAPSED_WIDTH,
  DASHBOARD_NAV_EXPANDED_WIDTH,
  DashboardHeaderConfig,
  useDashboardShell,
} from '../contexts/DashboardShellContext';
import {
  DASHBOARD_SCROLLED_GLASS_BORDER,
  DASHBOARD_SCROLLED_GLASS_SHADOW,
  getDashboardGlassBackground,
  getDashboardGlassFilter,
} from './common/dashboardGlass';
import {
  DashboardNavigationDrawer,
  DashboardNavigationRail,
} from './DashboardNavigation';
import AccountNavigation from './AccountNavigation';
import NavigationMenuButton from './common/NavigationMenuButton';
import { STUDIO_POC_ENABLED } from '../config/releaseFlags';

const TEXT_SECONDARY = '#B9BAC5';
const dashboardTitleSx = {
  color: '#fff',
  fontWeight: 900,
  fontSize: { xs: 24, sm: 28, md: 32 },
  lineHeight: 1.1,
  letterSpacing: '-0.02em',
};

const getFallbackHeader = (pathname: string): DashboardHeaderConfig => {
  if (pathname === '/my-stories') return { title: 'My Stories' };
  if (pathname === '/create') return { title: 'Create a Story' };
  if (pathname.startsWith('/create-next-chapter/'))
    return { title: 'Create Next Chapter' };
  if (pathname.startsWith('/story-preview/')) return { title: 'Story Preview' };
  if (pathname === '/characters') return { title: 'Your Characters' };
  if (pathname.startsWith('/characters/')) return { title: 'Character' };
  if (pathname === '/voices') return { title: 'Your Voices' };
  if (pathname.startsWith('/voices/')) return { title: 'Voice' };
  if (pathname === '/settings') return { title: 'Account' };
  if (pathname === '/buy-tokens') return { title: 'Buy Tokens' };
  if (pathname === '/manage-subscription') return { title: 'Subscription' };
  if (pathname.startsWith('/edit-story/')) return { title: 'Edit Story' };
  if (pathname === '/notifications') return { title: 'Notifications' };
  if (pathname === '/social-accounts') return { title: 'Social Accounts' };
  if (pathname === '/publishing-results') return { title: 'Publishing Results' };
  if (pathname === '/share') return { title: 'Share Story' };
  if (pathname === '/download') return { title: 'Download Story' };
  if (pathname.startsWith('/book-builder/cover'))
    return { title: 'Create Cover' };
  if (pathname.startsWith('/book-builder'))
    return { title: 'Create & Order Book' };
  if (pathname.startsWith('/my-orders/')) return { title: 'Order Details' };
  if (pathname === '/my-orders') return { title: 'My Orders' };
  if (pathname === '/support') return { title: 'Support' };
  return { title: 'Fable' };
};

const DashboardHeader: React.FC<{ scrollProgress?: number }> = ({
  scrollProgress = 0,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const { header, leftRailWidth, navigationOpen, setNavigationOpen } =
    useDashboardShell();
  const pageHeader = header || getFallbackHeader(location.pathname);
  const navigationWidth = navigationOpen
    ? DASHBOARD_NAV_EXPANDED_WIDTH
    : DASHBOARD_NAV_COLLAPSED_WIDTH;
  const leftAlignedHeader = pageHeader.align === 'left';
  const isCreatePage = location.pathname === '/create';
  const useCreateHeaderLayout = STUDIO_POC_ENABLED && isCreatePage;
  return (
    <>
      <Box
        component="header"
        data-dashboard-header
        sx={{
          position: 'fixed',
          container: useCreateHeaderLayout
            ? 'dashboard-header / inline-size'
            : undefined,
          top: 0,
          left: `${leftRailWidth}px`,
          right: desktop ? `${navigationWidth}px` : 0,
          zIndex: 1300,
          width: 'auto',
          height: 'var(--fable-dashboard-header-height)',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          background: getDashboardGlassBackground(scrollProgress),
          backdropFilter: getDashboardGlassFilter(scrollProgress),
          WebkitBackdropFilter: getDashboardGlassFilter(scrollProgress),
          borderBottom:
            scrollProgress > 0.15
              ? DASHBOARD_SCROLLED_GLASS_BORDER
              : '1px solid rgba(255,255,255,0.08)',
          boxShadow:
            scrollProgress > 0.5 ? DASHBOARD_SCROLLED_GLASS_SHADOW : 'none',
          transition: [
            'left 0.22s ease',
            'right 0.22s ease',
            'background 0.2s ease',
            'border-bottom 0.2s ease',
            'box-shadow 0.2s ease',
            'backdrop-filter 0.2s ease',
          ].join(', '),
        }}
      >
        {useCreateHeaderLayout ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '190px minmax(0, 1fr) 190px',
              alignItems: 'center',
              px: 2.5,
              '@container dashboard-header (max-width: 760px)': {
                gridTemplateColumns: '36px minmax(0, 1fr) 36px',
                alignContent: 'center',
                gap: 0.25,
                px: 1.5,
              },
            }}
          >
            <Typography
              component="h1"
              sx={{
                ...dashboardTitleSx,
                gridColumn: 2,
                gridRow: 1,
                minWidth: 0,
                textAlign: 'center',
                textWrap: 'balance',
              }}
            >
              {pageHeader.title}
            </Typography>
            <Box
              sx={{
                gridColumn: 3,
                gridRow: 1,
                justifySelf: 'end',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {!desktop && (
                <NavigationMenuButton
                  onClick={() => setMobileNavigationOpen(true)}
                />
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              height: '100%',
              pl: leftAlignedHeader
                ? { xs: 2.5, sm: 4 }
                : { xs: 7.5, sm: 9, md: 10 },
              pr: leftAlignedHeader
                ? { xs: 2.5, sm: 4 }
                : { xs: 7.5, sm: 9, md: 10 },
              py: { xs: 1.35, md: 1.5 },
              boxSizing: 'border-box',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: leftAlignedHeader ? 'flex-start' : 'center',
              gap: 0,
            }}
          >
            {pageHeader.back && (
              <Tooltip title={pageHeader.back.label || 'Back'}>
                <IconButton
                  aria-label={pageHeader.back.label || 'Back'}
                  onClick={pageHeader.back.onClick}
                  disableRipple
                  sx={{
                    position: 'absolute',
                    zIndex: 2,
                    left: { xs: 12, sm: 20, md: 24 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: { xs: 42, md: 48 },
                    height: { xs: 42, md: 48 },
                    color: '#fff',
                    bgcolor: 'transparent',
                    border: 0,
                    borderRadius: 0,
                    opacity: 0.9,
                    transition: 'opacity 160ms ease',
                    '&:hover': {
                      bgcolor: 'transparent',
                      opacity: 1,
                    },
                    '&.Mui-focusVisible': {
                      outline: '2px solid #D8A363',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: { xs: 27, md: 30 } }} />
                </IconButton>
              </Tooltip>
            )}

            <Box
              sx={{
                minWidth: 0,
                width: '100%',
                maxWidth: leftAlignedHeader
                  ? { xs: '100%', md: 960, lg: 1040 }
                  : { xs: '100%', md: 760, lg: 840 },
                mx: leftAlignedHeader ? 'auto' : 0,
                textAlign: leftAlignedHeader ? 'left' : 'center',
              }}
            >
              <Typography
                component="h1"
                noWrap
                sx={dashboardTitleSx}
              >
                {pageHeader.title}
              </Typography>
              {leftAlignedHeader && pageHeader.subtitle && (
                <Typography
                  component="div"
                  noWrap
                  sx={{
                    color: TEXT_SECONDARY,
                    fontSize: { xs: 13, sm: 14, md: 15 },
                    lineHeight: 1.35,
                    mt: 0.4,
                    textAlign: 'left',
                  }}
                >
                  {pageHeader.subtitle}
                </Typography>
              )}
            </Box>

            {(pageHeader.actions || !desktop) && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={{ xs: 0.75, sm: 1.25 }}
                sx={{
                  position: 'absolute',
                  zIndex: 2,
                  right: { xs: 12, sm: 20, md: 24 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                {pageHeader.actions}
                {!desktop && (
                  <NavigationMenuButton
                    onClick={() => setMobileNavigationOpen(true)}
                    sx={{
                      width: 42,
                      height: 42,
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>
        )}
      </Box>

      {desktop && (
        <DashboardNavigationRail expanded={navigationOpen}>
          <AccountNavigation
            expanded={navigationOpen}
            onToggleExpanded={setNavigationOpen}
          />
        </DashboardNavigationRail>
      )}

      <DashboardNavigationDrawer
        open={!desktop && mobileNavigationOpen}
        onClose={() => setMobileNavigationOpen(false)}
      >
        <AccountNavigation
          expanded
          mobile
          onClose={() => setMobileNavigationOpen(false)}
        />
      </DashboardNavigationDrawer>
    </>
  );
};

export default DashboardHeader;
